import { z } from 'zod';
import { flexibleBoolean } from '../zod-utils.js';
import { assessmentWithContentSchema } from './assessments.js';
import { filteringSchemaFrom, pagingSchema, typeaheadFilterSetSchema } from './modifier.js';
import { StudentYear } from './student.js';
import { paginatedRequest, paginatedResponse } from './util-schema-types.js';
export { lessonKindSchema, LessonKindToLabel } from './lesson-shared.js';
export type { LessonKind } from './lesson-shared.js';

export const lessonSchema = z.object({
  id: z.string(),
  name: z.string(),
  objectives: z.string().array(),
  handouts: z.string().array(),
  lessonLinkKTo2: z.string().nullable(),
  lessonLink3To5: z.string().nullable(),
  lessonLink6To8: z.string().nullable(),
  shouldSkipFeedback: z.boolean().nullable(),
  minWPM: z.number().nullable(),
  maxWPM: z.number().nullable(),
  minAccuracyPercent: z.number().nullable(),
  maxAccuracyPercent: z.number().nullable(),
  floridaStandard: z.string().optional().nullable(),
  arkansasStandard: z.string().optional().nullable(),
});

export type Lesson = z.infer<typeof lessonSchema>;

export const lessonWithAssessmentsSchema = lessonSchema.extend({
  assessments: z.array(z.lazy(() => assessmentWithContentSchema)),
  /** Learning path names that include this lesson (via `LearningPathLesson`). Used e.g. for fluency detection on sessions. */
  parentLearningPathNames: z.array(z.string()),
});
export type LessonWithAssessments = z.infer<typeof lessonWithAssessmentsSchema>;

export const createLessonOptionsSchema = z.object({
  name: z.string(),
  objectives: z.string().array().nullable(),
  handouts: z.string().array().nullable(),
  lessonLinkKTo2: z.string().nullable(),
  lessonLink3To5: z.string().nullable(),
  lessonLink6To8: z.string().nullable(),
  shouldSkipFeedback: z.boolean().nullable(),
  assessmentIds: z.string().array().nullable(),
  assessmentContentSelectionOverride: z
    .array(z.object({ assessmentId: z.string(), assessmentContentId: z.string() }))
    .optional()
    .nullable(),
  minWPM: z.number().nullable(),
  maxWPM: z.number().nullable(),
  minAccuracyPercent: z.number().nullable(),
  maxAccuracyPercent: z.number().nullable(),
  floridaStandard: z.string().optional().nullable(),
  arkansasStandard: z.string().optional().nullable(),
});
export type CreateLessonOptions = z.infer<typeof createLessonOptionsSchema>;

export const createLessonCsvRow = z.object({
  name: z.string().min(1, 'Lesson name is required'),
  objectives: z.string().nullable(),
  handouts: z.string().nullable(),
  lessonLinkKTo2: z.string().nullable(),
  lessonLink3To5: z.string().nullable(),
  lessonLink6To8: z.string().nullable(),
  assessmentNames: z.string().nullable(),
  shouldSkipFeedback: flexibleBoolean,
});
export type CreateLessonCsvRow = z.infer<typeof createLessonCsvRow>;

export const CreateLessonCsvFormKeys = {
  csvFile: 'csvFile',
} as const;

export const updateLessonOptionsSchema = z.object({
  name: z.string(),
  objectives: z.string().array().nullable(),
  handouts: z.string().array().nullable(),
  lessonLinkKTo2: z.string().nullable(),
  lessonLink3To5: z.string().nullable(),
  lessonLink6To8: z.string().nullable(),
  assessmentIds: z.string().array().nullable(),
  shouldSkipFeedback: z.boolean().optional().nullable(),
  minWPM: z.number().optional().nullable(),
  maxWPM: z.number().optional().nullable(),
  minAccuracyPercent: z.number().optional().nullable(),
  maxAccuracyPercent: z.number().optional().nullable(),
  floridaStandard: z.string().optional().nullable(),
  arkansasStandard: z.string().optional().nullable(),
});
export type UpdateLessonOptions = z.infer<typeof updateLessonOptionsSchema>;

export const getLessonsQuerySchema = z.object({
  query: z.string().optional(),
});

export type GetLessonsQuery = z.infer<typeof getLessonsQuerySchema>;

export const getLessonsPaginatedQuerySchema = paginatedRequest.extend(getLessonsQuerySchema.shape);
export type GetLessonsPaginatedQuery = z.infer<typeof getLessonsPaginatedQuerySchema>;

export const getLessonResultsSchema = paginatedResponse.extend({
  lessons: z.array(lessonWithAssessmentsSchema),
});

export type GetLessonResults = z.infer<typeof getLessonResultsSchema>;

export const getLessonsTypeaheadFilterSchema = typeaheadFilterSetSchema;
export type GetLessonsTypeaheadFilter = z.infer<typeof getLessonsTypeaheadFilterSchema>;

export const getLessonsTypeaheadModifierSchema = pagingSchema.merge(
  filteringSchemaFrom(getLessonsTypeaheadFilterSchema),
);
export type GetLessonsTypeaheadModifier = z.infer<typeof getLessonsTypeaheadModifierSchema>;

export const getLessonsTypeaheadResultsSchema = z.array(lessonSchema);
export type GetLessonsTypeaheadResults = z.infer<typeof getLessonsTypeaheadResultsSchema>;

export const getLessonLinkBasedOnStudentYear = (
  lessonWithLinks: Pick<Lesson, 'lessonLinkKTo2' | 'lessonLink3To5' | 'lessonLink6To8'>,
  studentYearToCheck: StudentYear | null | undefined,
): string | null => {
  if (
    studentYearToCheck === 'Pre_K' || // Pre-K also uses K-2 link
    studentYearToCheck === 'K' ||
    studentYearToCheck === 'first' ||
    studentYearToCheck === 'second'
  ) {
    return lessonWithLinks.lessonLinkKTo2;
  }
  if (studentYearToCheck === 'third' || studentYearToCheck === 'fourth' || studentYearToCheck === 'fifth') {
    return lessonWithLinks.lessonLink3To5;
  }
  if (studentYearToCheck === 'sixth' || studentYearToCheck === 'seventh' || studentYearToCheck === 'eighth') {
    return lessonWithLinks.lessonLink6To8;
  }

  // Default to Lesson link 3-5 for grade 9 - 13 or grade is empty
  return lessonWithLinks.lessonLink3To5;
};

export const isGLALesson = (lessonName: string | null | undefined) => {
  if (!lessonName) return false;
  return lessonName.includes('(GLA)');
};
